import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { useEditor, EditorContent as TipTapEditorContent, generateHTML } from '@tiptap/react'
import * as DOMPurify from 'dompurify'
import { isRtlSelector } from 'redux/selectors/general'
import { RichTextEditorProps, EditorContent as EditorContentType } from './types'
import { Toolbar } from './Toolbar'
import { richTextEditorExtensions } from './extensions'
import { DEFAULT_EDITOR_STYLES, ALLOWED_HTML_TAGS, ALLOWED_HTML_ATTRS } from './constants'
import './styles.css'

/**
 * RichTextEditor Component - Optimized Version
 *
 * A rich text editor built with TipTap v3 and React.
 * Supports both edit and read-only modes.
 *
 * **IMPORTANT**: Component uses JSON format for content (TipTap/ProseMirror structure)
 * - Input: JSON object representing document structure
 * - Output: JSON object via onChange callback
 * - Storage: Store JSON object in your state/database
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  editable = true,
  placeholder = 'Start typing...',
  containerStyle = {},
  className = '',
  style = {},
  showToolbar = true,
  toolbarVisibility = 'focus',
  onFocus,
  onBlur,
  disabled = false,
  contentMinHeight,
}) => {
  // Get RTL direction from Redux
  const isRtl = useSelector(isRtlSelector)

  const [isFocused, setIsFocused] = useState(false)

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: richTextEditorExtensions,
    content: value || undefined,
    editable: editable && !disabled,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON()
      onChange?.(json as EditorContentType)
    },
    onFocus: () => {
      setIsFocused(true)
      onFocus?.()
    },
    onBlur: () => {
      setIsFocused(false)
      onBlur?.()
    },
    editorProps: {
      attributes: {
        class: 'rich-text-editor-content',
        dir: isRtl ? 'rtl' : 'ltr',
        ...(contentMinHeight ? { style: `min-height: ${contentMinHeight}` } : {}),
      },
      // Handle paste: convert all pasted content to plain text (remove all styles)
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain')
        if (text) {
          // Insert only plain text without any formatting
          const { state } = view
          const { tr } = state
          tr.insertText(text)
          view.dispatch(tr)
          return true // Prevent default paste behavior
        }
        return false
      },
    },
  })

  // Combined container styles
  const containerStyles = useMemo(
    () => ({
      ...DEFAULT_EDITOR_STYLES,
      ...containerStyle,
      ...style,
    }),
    [containerStyle, style]
  )

  // Sync content when value changes externally
  useEffect(() => {
    if (!editor) return

    if (!value) {
      if (!editor.isEmpty) editor.commands.clearContent()
      return
    }

    const currentContent = editor.getJSON()
    const hasChanged = JSON.stringify(currentContent) !== JSON.stringify(value)

    if (hasChanged) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  // Sync editable mode
  useEffect(() => {
    if (!editor) return
    editor.setEditable(editable && !disabled)
  }, [editable, disabled, editor])

  // Sync text direction
  useEffect(() => {
    if (!editor) return
    editor.view.dom.setAttribute('dir', isRtl ? 'rtl' : 'ltr')
  }, [isRtl, editor])

  // Read-only mode: render sanitized HTML
  if (!editable) {
    const htmlContent = value ? generateHTML(value as any, richTextEditorExtensions) : '<p></p>'

    const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [...ALLOWED_HTML_TAGS],
      ALLOWED_ATTR: [...ALLOWED_HTML_ATTRS],
    })

    return (
      <Box
        className={`rich-text-editor-readonly ${className} ${isRtl ? 'rtl' : 'ltr'}`}
        dir={isRtl ? 'rtl' : 'ltr'}
        style={containerStyles as any}
      >
        <Box
          className="rich-text-editor-content"
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
          style={contentMinHeight !== undefined ? { minHeight: contentMinHeight } : undefined}
        />
      </Box>
    )
  }

  // Edit mode: render editor with toolbar
  if (!editor) {
    return null
  }

  return (
    <Box
      className={`rich-text-editor ${className} ${disabled ? 'disabled' : ''} ${isRtl ? 'rtl' : 'ltr'}`}
      dir={isRtl ? 'rtl' : 'ltr'}
      style={containerStyles as any}
    >
      <Box className="rich-text-editor-wrapper">
        <TipTapEditorContent editor={editor} />
        {editor.isEmpty && placeholder && <Box className="rich-text-editor-placeholder">{placeholder}</Box>}
      </Box>
      {showToolbar && (
        <Box
          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
          style={{
            maxHeight: toolbarVisibility === 'always' || isFocused ? '37px' : '0',
            overflow: 'hidden',
            opacity: toolbarVisibility === 'always' || isFocused ? 1 : 0,
            transition: 'max-height 0.2s ease, opacity 0.2s ease',
          }}
        >
          <Toolbar editor={editor} isRtl={isRtl} />
        </Box>
      )}
    </Box>
  )
}
